(function () {

  /*
   * ============================================================
   * WARLOND PLASTICS
   * CHEMICAL RESISTANCE GUIDE — VERSION 3.2
   * ============================================================
   *
   * V3.2:
   * - Keeps "Best Starting Option"
   * - Condition-aware recommendations
   * - More practical alcohol recommendations
   * - Petrol/Gasoline: PTFE > PEEK > Acetal
   * - Concentrated acetic acid revised
   * - Phosphoric acid revised
   * - Hydrogen peroxide revised
   * - Conservative treatment above reference temperature ranges
   * - Fuel / solvent mixture warnings
   * - Does not force three materials when conditions are demanding
   *
   * This guide provides preliminary material-selection guidance.
   * It does not approve, certify or guarantee a finished application.
   * ============================================================
   */


  function initChemicalGuide(root) {

    if (!root || root.dataset.wpInitialised === 'true') {
      return;
    }


    /* =========================================================
       ELEMENTS
       ========================================================= */

    const checkButton =
      root.querySelector('[data-wp-check]');

    const results =
      root.querySelector('[data-wp-results]');

    const entered =
      root.querySelector('[data-wp-entered]');

    const confidenceBox =
      root.querySelector('[data-wp-confidence]');

    const bestContainer =
      root.querySelector('[data-wp-best]');

    const alternativesContainer =
      root.querySelector('[data-wp-alternatives]');

    const chemicalEl =
      root.querySelector('[data-wp-chemical]');

    const concentrationEl =
      root.querySelector('[data-wp-concentration]');

    const temperatureEl =
      root.querySelector('[data-wp-temperature]');

    const exposureEl =
      root.querySelector('[data-wp-exposure]');


    if (
      !checkButton ||
      !results ||
      !entered ||
      !confidenceBox ||
      !bestContainer ||
      !alternativesContainer ||
      !chemicalEl ||
      !concentrationEl ||
      !temperatureEl ||
      !exposureEl
    ) {
      return;
    }


    root.dataset.wpInitialised = 'true';


    /* =========================================================
       WARLOND PLASTICS COLLECTION LINKS
       ========================================================= */

    const links = {

      PTFE:
        '/collections/ptfe',

      PEEK:
        '/collections/peek',

      HDPE:
        '/collections/hdpe-rod-sheet',

      PP:
        '/collections/polypropylene',

      PVC:
        '/collections/pvc',

      Acetal:
        '/collections/acetal',

      Nylon:
        '/collections/nylon-rod',

      Acrylic:
        '/collections/acrylic-sheet-rod',

      Polycarbonate:
        '/collections/polycarbonate-sheets',

      ABS:
        '/collections/black-abs-plastic-sheet-haircell-finish'

    };


    /* =========================================================
       BROAD STARTING DATABASE
       ========================================================= */

    const recommendations = {


      hydrochloric: {

        best:
          'PTFE',

        alternatives: [
          'HDPE',
          'PP'
        ],

        reason:
          'PTFE is the strongest broad starting option for hydrochloric acid. HDPE and Polypropylene can also be practical lower-cost alternatives depending on concentration, temperature and exposure.'

      },


      sulphuric: {

        best:
          'PTFE',

        alternatives: [
          'HDPE',
          'PP'
        ],

        reason:
          'PTFE is the strongest broad starting option for sulphuric acid. HDPE and Polypropylene can also be useful alternatives under suitable concentration and temperature conditions.'

      },


      phosphoric: {

        best:
          'PTFE',

        alternatives: [
          'HDPE',
          'PP'
        ],

        reason:
          'PTFE provides a strong broad starting option for phosphoric acid. HDPE and Polypropylene can also be practical alternatives depending on concentration, temperature and exposure.'

      },


      citric: {

        best:
          'HDPE',

        alternatives: [
          'PP',
          'PTFE'
        ],

        reason:
          'HDPE is a strong practical starting option for many citric acid environments. Polypropylene and PTFE are also useful alternatives.'

      },


      acetic: {

        best:
          'PTFE',

        alternatives: [
          'HDPE',
          'PP'
        ],

        reason:
          'PTFE is a strong broad starting option for acetic acid. HDPE and Polypropylene can also be practical alternatives under suitable concentration and temperature conditions.'

      },


      'sodium-hydroxide': {

        best:
          'HDPE',

        alternatives: [
          'PP',
          'PTFE'
        ],

        reason:
          'HDPE is often a strong practical starting option for caustic soda environments. Polypropylene and PTFE are also commonly worth considering.'

      },


      'potassium-hydroxide': {

        best:
          'HDPE',

        alternatives: [
          'PP',
          'PTFE'
        ],

        reason:
          'HDPE is a practical starting option for many alkaline environments. Polypropylene and PTFE are also useful alternatives.'

      },


      ammonia: {

        best:
          'PTFE',

        alternatives: [
          'HDPE',
          'PP'
        ],

        reason:
          'PTFE provides very broad chemical resistance. HDPE and Polypropylene may provide more economical alternatives depending on concentration and temperature.'

      },


      bleach: {

        best:
          'PTFE',

        alternatives: [
          'PVC',
          'HDPE'
        ],

        reason:
          'PTFE is a strong starting option for sodium hypochlorite and bleach environments. PVC and HDPE can also be useful alternatives under suitable concentration and temperature conditions.'

      },


      peroxide: {

        best:
          'PTFE',

        alternatives: [
          'HDPE',
          'PVC'
        ],

        reason:
          'PTFE is a strong broad starting option for hydrogen peroxide. HDPE and PVC can also be useful alternatives under suitable concentration and temperature conditions.'

      },


      /*
       * V3.2:
       * PP becomes the practical first option for common
       * room-temperature alcohol service.
       */

      ethanol: {

        best:
          'PP',

        alternatives: [
          'Acetal',
          'PTFE'
        ],

        reason:
          'Polypropylene is a practical starting option for many ethanol applications at normal operating temperatures. Acetal is another useful engineering option, while PTFE provides the broadest chemical-resistance alternative.'

      },


      ipa: {

        best:
          'PP',

        alternatives: [
          'Acetal',
          'PTFE'
        ],

        reason:
          'Polypropylene is a practical starting option for many isopropyl alcohol applications at normal operating temperatures. Acetal is also useful where greater stiffness and dimensional stability are required, while PTFE provides broader chemical resistance.'

      },


      methanol: {

        best:
          'PP',

        alternatives: [
          'Acetal',
          'PTFE'
        ],

        reason:
          'Polypropylene is a practical starting option for many methanol applications at normal operating temperatures. Acetal and PTFE are also useful alternatives depending on the mechanical and service requirements.'

      },


      acetone: {

        best:
          'PTFE',

        alternatives: [
          'PEEK',
          'Acetal'
        ],

        reason:
          'PTFE is one of the strongest starting options for acetone. PEEK is another high-performance option. Acetal may also be useful under suitable conditions. Acrylic, Polycarbonate and ABS are generally poor starting materials for acetone exposure.'

      },


      mek: {

        best:
          'PTFE',

        alternatives: [
          'PEEK'
        ],

        reason:
          'PTFE is the strongest broad starting option for MEK. PEEK is another high-performance option. Many common plastics are vulnerable to ketone solvents.'

      },


      toluene: {

        best:
          'PTFE',

        alternatives: [
          'PEEK'
        ],

        reason:
          'PTFE is a strong starting option for toluene exposure. PEEK is another high-performance alternative where greater mechanical performance is required.'

      },


      xylene: {

        best:
          'PTFE',

        alternatives: [
          'PEEK'
        ],

        reason:
          'PTFE is one of the strongest broad starting options for xylene exposure. PEEK is another high-performance material to consider.'

      },


      /*
       * V3.2:
       * Acetal restored as a practical third option.
       */

      petrol: {

        best:
          'PTFE',

        alternatives: [
          'PEEK',
          'Acetal'
        ],

        reason:
          'PTFE is the strongest broad chemical-resistance starting option for petrol and gasoline exposure. PEEK is another high-performance option where greater stiffness or strength is required. Acetal may also be a practical engineering option depending on the exact fuel formulation and application.'

      },


      diesel: {

        best:
          'Acetal',

        alternatives: [
          'Nylon',
          'PTFE'
        ],

        reason:
          'Acetal is a strong practical starting option for many diesel and fuel-related mechanical components. Nylon and PTFE are also useful alternatives.'

      },


      'mineral-oil': {

        best:
          'Acetal',

        alternatives: [
          'Nylon',
          'PTFE'
        ],

        reason:
          'Acetal is a strong starting option for many lubricated mechanical applications. Nylon and PTFE are also useful materials for many oil environments.'

      },


      glycol: {

        best:
          'HDPE',

        alternatives: [
          'PP',
          'PTFE'
        ],

        reason:
          'HDPE is a useful practical starting option for many glycol and coolant environments. Polypropylene and PTFE are also useful alternatives.'

      },


      water: {

        best:
          'HDPE',

        alternatives: [
          'PP',
          'Acetal'
        ],

        reason:
          'HDPE is a strong general starting option for water exposure because of its low water absorption. Polypropylene and Acetal are also useful alternatives.'

      },


      'salt-water': {

        best:
          'HDPE',

        alternatives: [
          'PP',
          'PTFE'
        ],

        reason:
          'HDPE is a strong practical starting option for salt-water environments. Polypropylene and PTFE are also useful alternatives.'

      }

    };


    /* =========================================================
       HELPERS
       ========================================================= */

    function numericValue(value) {

      if (
        value === '' ||
        value === null ||
        typeof value === 'undefined'
      ) {
        return null;
      }

      const parsed =
        Number(value);

      if (Number.isNaN(parsed)) {
        return null;
      }

      return parsed;

    }


    function copyRecommendation(base) {

      return {

        best:
          base.best,

        alternatives:
          base.alternatives.slice(),

        reason:
          base.reason,

        conditionMessage:
          '',

        specialist:
          false,

        alternativeDescriptions:
          {}

      };

    }


    function appendCondition(result, message) {

      if (!message) {
        return;
      }

      if (result.conditionMessage) {

        result.conditionMessage +=
          ' ' + message;

      }

      else {

        result.conditionMessage =
          message;

      }

    }


    function isOrganicSolvent(chemical) {

      return [

        'ethanol',
        'ipa',
        'methanol',
        'acetone',
        'mek',
        'toluene',
        'xylene'

      ].indexOf(chemical) !== -1;

    }


    function isFormulatedProduct(chemical) {

      return [

        'petrol',
        'diesel',
        'mineral-oil',
        'glycol'

      ].indexOf(chemical) !== -1;

    }


    /* =========================================================
       V3.2 CONDITION-AWARE ENGINE
       ========================================================= */

    function getConditionAwareRecommendation(
      chemical,
      concentrationInput,
      temperatureInput,
      exposure
    ) {

      const base =
        recommendations[chemical];

      if (!base) {
        return null;
      }

      const result =
        copyRecommendation(base);

      const concentration =
        numericValue(concentrationInput);

      const temperature =
        numericValue(temperatureInput);

      const continuous =
        exposure === 'continuous';

      const intermittent =
        exposure === 'intermittent';

      const vapour =
        exposure === 'vapour';


      /* =======================================================
         HYDROCHLORIC ACID
         ======================================================= */

      if (chemical === 'hydrochloric') {

        if (
          (
            concentration !== null &&
            concentration >= 35
          ) ||
          (
            temperature !== null &&
            temperature > 50
          )
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE',
            'PP'
          ];

          result.reason =
            'PTFE remains the strongest broad starting option for the hydrochloric acid conditions entered. HDPE and Polypropylene may still be useful alternatives, but exact service conditions become increasingly important at higher concentration or temperature.';

          appendCondition(
            result,
            'Higher hydrochloric acid concentration or temperature makes the exact operating conditions more important.'
          );

        }

      }


      /* =======================================================
         SULPHURIC ACID
         ======================================================= */

      if (chemical === 'sulphuric') {

        const highlyConcentrated =
          concentration !== null &&
          concentration >= 90;

        const aboveReferenceTemperature =
          temperature !== null &&
          temperature > 50;

        const demandingContinuous =
          continuous &&
          (
            (
              concentration !== null &&
              concentration >= 70
            ) ||
            (
              temperature !== null &&
              temperature >= 40
            )
          );


        if (
          highlyConcentrated ||
          aboveReferenceTemperature ||
          demandingContinuous
        ) {

          result.best =
            'PTFE';

          result.alternatives =
            [];

          result.reason =
            'PTFE is the strongest starting option for the sulphuric acid conditions entered. At very high concentration, elevated temperature or demanding continuous exposure, this guide does not present HDPE or Polypropylene as general alternatives.';

          appendCondition(
            result,
            'These sulphuric acid conditions require a more conservative material selection.'
          );

          result.specialist =
            true;

        }

        else if (
          concentration !== null &&
          concentration >= 60
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE'
          ];

          result.reason =
            'PTFE is the strongest starting option for this sulphuric acid concentration. HDPE may also be worth considering, but concentration, temperature and exposure should be checked before final selection.';

          appendCondition(
            result,
            'The sulphuric acid concentration entered reduces the number of broad alternative materials shown by this guide.'
          );

        }

      }


      /* =======================================================
         PHOSPHORIC ACID
         ======================================================= */

      if (chemical === 'phosphoric') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for phosphoric acid at the elevated temperature entered. HDPE may also remain worth considering, but the temperature is above the main comparison range used by this guide.';

          appendCondition(
            result,
            'The temperature entered is above the main reference range used for this phosphoric acid comparison.'
          );

          result.specialist =
            true;

        }

        else if (
          concentration !== null &&
          concentration >= 80
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE',
            'PP'
          ];

          result.reason =
            'PTFE is a strong broad starting option for concentrated phosphoric acid at approximately room temperature. HDPE and Polypropylene may also be practical alternatives under suitable service conditions.';

          appendCondition(
            result,
            'The concentration entered is high, so temperature and exposure remain important when making the final material choice.'
          );

        }

      }


      /* =======================================================
         CITRIC ACID
         ======================================================= */

      if (chemical === 'citric') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          appendCondition(
            result,
            'The temperature entered is above the main comparison range used by this guide, so the shortlist should be treated as preliminary.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         ACETIC ACID — V3.2 REVISED
         ======================================================= */

      if (chemical === 'acetic') {

        /*
         * Concentration alone does not collapse the result
         * to PTFE-only at room temperature.
         */

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE'
          ];

          result.reason =
            'PTFE is the most conservative broad starting option for acetic acid above the main temperature range used by this guide. HDPE may also remain worth considering, but the exact service conditions should be checked.';

          appendCondition(
            result,
            'The temperature entered is above the main reference range used for this acetic acid comparison.'
          );

          result.specialist =
            true;

        }

        else if (
          concentration !== null &&
          concentration >= 80
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE',
            'PP'
          ];

          result.reason =
            'PTFE is a strong broad starting option for concentrated acetic acid at approximately room temperature. HDPE and Polypropylene may also be useful alternatives under suitable service conditions.';

          appendCondition(
            result,
            'The acetic acid concentration entered is high, so temperature and exposure should also be considered.'
          );

        }

      }


      /* =======================================================
         SODIUM HYDROXIDE
         ======================================================= */

      if (chemical === 'sodium-hydroxide') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PP'
          ];

          result.reason =
            'At the elevated temperature entered, PTFE is the most conservative broad starting option shown by this guide. Polypropylene may also be worth considering, but the temperature is above the main comparison range used for this recommendation.';

          appendCondition(
            result,
            'The temperature entered is above the main reference range used by this guide for caustic soda.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         POTASSIUM HYDROXIDE
         ======================================================= */

      if (chemical === 'potassium-hydroxide') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PP'
          ];

          result.reason =
            'At the elevated temperature entered, PTFE is the most conservative broad starting option shown by this guide. Polypropylene may also be worth considering, but the exact high-temperature conditions should be checked.';

          appendCondition(
            result,
            'The temperature entered is above the main reference range used for this comparison.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         AMMONIA
         ======================================================= */

      if (chemical === 'ammonia') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PP'
          ];

          result.reason =
            'PTFE remains the most conservative broad starting option for ammonia at the elevated temperature entered. Polypropylene may also be worth considering depending on the complete application.';

          appendCondition(
            result,
            'The elevated temperature reduces the number of broad lower-cost alternatives shown.'
          );

        }

      }


      /* =======================================================
         BLEACH / SODIUM HYPOCHLORITE
         ======================================================= */

      if (chemical === 'bleach') {

        const concentratedBleach =
          concentration !== null &&
          concentration >= 15;

        const warmBleach =
          temperature !== null &&
          temperature >= 40;


        if (
          (
            concentratedBleach &&
            warmBleach
          ) ||
          (
            continuous &&
            (
              concentratedBleach ||
              warmBleach
            )
          )
        ) {

          result.best =
            'PTFE';

          result.alternatives =
            [];

          result.reason =
            'PTFE is the strongest broad starting option for the sodium hypochlorite conditions entered. Higher concentration, temperature or continuous oxidising exposure makes alternative-material selection more condition-sensitive.';

          appendCondition(
            result,
            'These bleach conditions are demanding enough that this guide does not provide a general alternative material.'
          );

          result.specialist =
            true;

        }

        else if (
          concentratedBleach ||
          warmBleach
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PVC'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for the bleach conditions entered. PVC may also be worth considering, while HDPE has been removed from the broad shortlist because the concentration or temperature is more demanding.';

          appendCondition(
            result,
            'The entered bleach concentration or temperature reduces the broad material shortlist.'
          );

        }

      }


      /* =======================================================
         HYDROGEN PEROXIDE
         ======================================================= */

      if (chemical === 'peroxide') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE'
          ];

          result.reason =
            'PTFE is the most conservative broad starting option for hydrogen peroxide above the main temperature range used by this guide. HDPE may also remain worth considering, but the exact concentration and service conditions should be checked.';

          appendCondition(
            result,
            'The temperature entered is above the main reference range used for this hydrogen peroxide comparison.'
          );

          result.specialist =
            true;

        }

        else if (
          concentration !== null &&
          concentration >= 30 &&
          temperature !== null &&
          temperature >= 40
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for concentrated hydrogen peroxide at the elevated temperature entered. HDPE may also be worth considering under suitable service conditions.';

          appendCondition(
            result,
            'Concentration and elevated temperature make the peroxide application more condition-sensitive.'
          );

        }

        else if (
          concentration !== null &&
          concentration >= 30
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'HDPE',
            'PVC'
          ];

          result.reason =
            'PTFE is a strong broad starting option for hydrogen peroxide at the concentration entered. HDPE and PVC may also be useful alternatives at suitable temperatures and exposure conditions.';

          appendCondition(
            result,
            'Hydrogen peroxide concentration should be considered together with temperature and duration of exposure.'
          );

        }

      }


      /* =======================================================
         ETHANOL — V3.2
         ======================================================= */

      if (chemical === 'ethanol') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PP'
          ];

          result.reason =
            'At the elevated temperature entered, PTFE becomes the more conservative broad starting option for ethanol. Polypropylene may also remain worth considering depending on its temperature and mechanical requirements.';

          appendCondition(
            result,
            'Elevated temperature reduces the broad alternative shortlist.'
          );

        }

      }


      /* =======================================================
         IPA — V3.2
         ======================================================= */

      if (chemical === 'ipa') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PP'
          ];

          result.reason =
            'At the elevated temperature entered, PTFE becomes the more conservative broad starting option for isopropyl alcohol. Polypropylene may also remain worth considering depending on the complete application.';

          appendCondition(
            result,
            'Elevated temperature reduces the broad alternative shortlist.'
          );

        }

      }


      /* =======================================================
         METHANOL — V3.2
         ======================================================= */

      if (chemical === 'methanol') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PP'
          ];

          result.reason =
            'At the elevated temperature entered, PTFE becomes the more conservative broad starting option for methanol. Polypropylene may also remain worth considering depending on the complete application.';

          appendCondition(
            result,
            'Elevated temperature reduces the broad alternative shortlist.'
          );

        }

      }


      /* =======================================================
         ACETONE
         ======================================================= */

      if (chemical === 'acetone') {

        /*
         * At ordinary/room-temperature conditions:
         * PTFE > PEEK > Acetal
         *
         * At clearly more demanding elevated continuous service:
         * narrow to PTFE > PEEK.
         */

        if (
          continuous &&
          temperature !== null &&
          temperature > 40
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PEEK'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for acetone under the more demanding elevated-temperature continuous conditions entered. PEEK is another high-performance alternative.';

          appendCondition(
            result,
            'Elevated temperature combined with continuous acetone exposure has reduced the material shortlist.'
          );

        }

        else if (continuous) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PEEK',
            'Acetal'
          ];

          result.reason =
            'PTFE is the strongest starting option for continuous acetone exposure. PEEK is another high-performance option. Acetal may also be worth considering at suitable temperatures, but prolonged exposure and mechanical stress should be considered.';

          result.alternativeDescriptions.Acetal =
            'Acetal may be a possible alternative for acetone under suitable conditions, but prolonged exposure, operating temperature and mechanical stress should be considered before final material selection.';

          appendCondition(
            result,
            'Continuous acetone exposure requires greater attention to the finished component and mechanical requirements.'
          );

        }

      }


      /* =======================================================
         MEK
         ======================================================= */

      if (chemical === 'mek') {

        if (
          continuous ||
          (
            temperature !== null &&
            temperature > 40
          )
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PEEK'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for the MEK conditions entered. PEEK is the other high-performance option shown by this guide for demanding exposure.';

          appendCondition(
            result,
            'MEK is an aggressive solvent for many common plastics, so the shortlist remains deliberately narrow.'
          );

        }

      }


      /* =======================================================
         TOLUENE
         ======================================================= */

      if (chemical === 'toluene') {

        if (
          continuous ||
          (
            temperature !== null &&
            temperature > 40
          )
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PEEK'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for demanding toluene exposure. PEEK is the other high-performance material shown by this guide.';

          appendCondition(
            result,
            'Toluene can attack or swell a number of common plastics, so the material shortlist remains deliberately narrow.'
          );

        }

      }


      /* =======================================================
         XYLENE
         ======================================================= */

      if (chemical === 'xylene') {

        if (
          continuous ||
          (
            temperature !== null &&
            temperature > 40
          )
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PEEK'
          ];

          result.reason =
            'PTFE is the strongest broad starting option for demanding xylene exposure. PEEK is the other high-performance material shown by this guide.';

          appendCondition(
            result,
            'Xylene can attack or swell a number of common plastics, so the material shortlist remains deliberately narrow.'
          );

        }

      }


      /* =======================================================
         PETROL / GASOLINE — V3.2
         ======================================================= */

      if (chemical === 'petrol') {

        result.best =
          'PTFE';

        result.alternatives = [
          'PEEK',
          'Acetal'
        ];

        result.reason =
          'PTFE is the strongest broad chemical-resistance starting option for petrol and gasoline exposure. PEEK is another high-performance option where greater stiffness or strength is required. Acetal may also be a practical engineering option depending on the exact fuel formulation and service conditions.';

        result.alternativeDescriptions.Acetal =
          'Acetal may be a practical option for many fuel-related mechanical components, but commercial petrol and gasoline formulations vary, so the exact fuel blend and finished application should be considered.';


        appendCondition(
          result,
          'Commercial petrol and gasoline are mixtures and formulations can vary, including blended components such as ethanol.'
        );


        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.alternatives = [
            'PEEK'
          ];

          result.reason =
            'At the elevated fuel temperature entered, PTFE is the most conservative broad chemical-resistance starting option shown by this guide. PEEK is another high-performance option where greater mechanical performance is required.';

          appendCondition(
            result,
            'The elevated temperature entered has removed Acetal from the broad shortlist.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         DIESEL
         ======================================================= */

      if (chemical === 'diesel') {

        appendCondition(
          result,
          'Diesel and fuel formulations can vary, so the exact fuel and finished application should be considered.'
        );


        if (
          temperature !== null &&
          temperature > 50
        ) {

          result.best =
            'PTFE';

          result.alternatives = [
            'PEEK'
          ];

          result.reason =
            'At the elevated temperature entered, PTFE becomes the most conservative broad chemical-resistance starting option in this guide. PEEK is another high-performance option where greater mechanical performance is required.';

          appendCondition(
            result,
            'The temperature entered is above the normal comparison range used for the standard Acetal and Nylon starting options.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         MINERAL / LUBRICATING OIL
         ======================================================= */

      if (chemical === 'mineral-oil') {

        appendCondition(
          result,
          'Lubricating oils can contain additives, so the exact oil formulation may affect compatibility.'
        );


        if (
          temperature !== null &&
          temperature > 50
        ) {

          appendCondition(
            result,
            'The elevated temperature entered requires closer checking of material service temperature and mechanical load.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         GLYCOL / COOLANT
         ======================================================= */

      if (chemical === 'glycol') {

        appendCondition(
          result,
          'Commercial coolants can contain corrosion inhibitors and other additives, so the complete coolant formulation should be considered.'
        );


        if (
          temperature !== null &&
          temperature > 50
        ) {

          appendCondition(
            result,
            'The temperature entered is above the main comparison range used by this guide, so the shortlist should be treated as preliminary.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         WATER
         ======================================================= */

      if (chemical === 'water') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          appendCondition(
            result,
            'At elevated water temperature, service temperature, mechanical loading and dimensional requirements become increasingly important.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         SALT WATER
         ======================================================= */

      if (chemical === 'salt-water') {

        if (
          temperature !== null &&
          temperature > 50
        ) {

          appendCondition(
            result,
            'At elevated salt-water temperature, service temperature and the complete application should be checked before final material selection.'
          );

          result.specialist =
            true;

        }

      }


      /* =======================================================
         DILUTED SOLVENT / MIXTURE WARNING
         ======================================================= */

      if (
        isOrganicSolvent(chemical) &&
        concentration !== null &&
        concentration > 0 &&
        concentration < 100
      ) {

        appendCondition(
          result,
          'Because the solvent concentration is below 100%, the balance or carrier liquid can also affect compatibility.'
        );

      }


      /* =======================================================
         FORMULATED PRODUCT WARNING
         ======================================================= */

      if (
        isFormulatedProduct(chemical) &&
        concentration !== null
      ) {

        appendCondition(
          result,
          'For formulated products, a percentage alone may not fully describe the chemical environment.'
        );

      }


      /* =======================================================
         VAPOUR / FUME EXPOSURE
         ======================================================= */

      if (vapour) {

        appendCondition(
          result,
          'Vapour and fume exposure can behave differently from direct liquid contact and should be considered when assessing the finished component.'
        );

      }


      /* =======================================================
         REPEATED INTERMITTENT EXPOSURE
         ======================================================= */

      if (
        intermittent &&
        !result.conditionMessage
      ) {

        appendCondition(
          result,
          'Repeated intermittent exposure can behave differently from a single brief contact and should be considered when assessing the finished component.'
        );

      }


      return result;

    }


    /* =========================================================
       CONFIDENCE / GUIDANCE MESSAGE
       ========================================================= */

    function getConfidence(
      chemical,
      concentration,
      temperature,
      exposure,
      result
    ) {

      if (
        result &&
        result.specialist
      ) {

        return {

          title:
            'Closer condition check recommended',

          text:
            result.conditionMessage ||
            'The conditions entered require a more conservative material selection. Review the complete application before final material selection.'

        };

      }


      if (
        concentration === '' &&
        temperature === '' &&
        exposure === ''
      ) {

        return {

          title:
            'General guidance',

          text:
            'No operating conditions were provided, so these are broad material-selection starting points only.'

        };

      }


      if (
        concentration === '' ||
        temperature === '' ||
        exposure === ''
      ) {

        return {

          title:
            'More details would improve the comparison',

          text:
            result &&
            result.conditionMessage
              ?
              result.conditionMessage +
              ' One or more operating details are still missing.'
              :
              'Some useful operating information was provided, but one or more details are missing. The materials below remain useful starting options.'

        };

      }


      if (
        result &&
        result.conditionMessage
      ) {

        return {

          title:
            'Conditions considered',

          text:
            result.conditionMessage

        };

      }


      if (
        Number(temperature) > 40 ||
        exposure === 'continuous' ||
        exposure === 'vapour'
      ) {

        return {

          title:
            'Check conditions before final selection',

          text:
            'The operating conditions are more demanding. The materials below remain useful starting options, but the complete application should be considered before final material selection.'

        };

      }


      return {

        title:
          'Useful starting point',

        text:
          'The details supplied help narrow down the materials worth considering. Final suitability still depends on the complete application.'

      };

    }


    /* =========================================================
       RESULT CARD
       ========================================================= */

    function buildCard(
      material,
      label,
      description,
      best
    ) {

      const link =
        links[material];

      const wrapperClass =
        best
          ?
          'wp-chem-best'
          :
          'wp-chem-alt';

      const labelClass =
        best
          ?
          'wp-chem-best-label'
          :
          'wp-chem-alt-label';

      let linkHtml =
        '';


      if (link) {

        linkHtml =
          '<a class="wp-chem-link" href="' +
          link +
          '">' +
          'Explore ' +
          material +
          ' →</a>';

      }


      return (

        '<div class="' +
        wrapperClass +
        '">' +

          '<span class="' +
          labelClass +
          '">' +
            label +
          '</span>' +

          '<h3>' +
            material +
          '</h3>' +

          '<p>' +
            description +
          '</p>' +

          linkHtml +

        '</div>'

      );

    }


    /* =========================================================
       CHECK MATERIALS BUTTON
       ========================================================= */

    checkButton.addEventListener(
      'click',
      function () {


        const chemical =
          chemicalEl.value;

        const concentration =
          concentrationEl.value.trim();

        const temperature =
          temperatureEl.value.trim();

        const exposure =
          exposureEl.value;


        /* Chemical required */

        if (!chemical) {

          chemicalEl.focus();

          return;

        }


        /* Concentration validation */

        const concentrationNumber =
          numericValue(concentration);


        if (
          concentrationNumber !== null &&
          (
            concentrationNumber < 0 ||
            concentrationNumber > 100
          )
        ) {

          concentrationEl.focus();

          return;

        }


        /* Display entered conditions */

        const chemicalName =
          chemicalEl.options[
            chemicalEl.selectedIndex
          ].text;


        const exposureName =
          exposure
            ?
            exposureEl.options[
              exposureEl.selectedIndex
            ].text
            :
            'exposure not provided';


        entered.textContent =

          chemicalName +

          (
            concentration
              ?
              ' · ' +
              concentration +
              '%'
              :
              ''
          ) +

          (
            temperature
              ?
              ' · ' +
              temperature +
              '°C'
              :
              ''
          ) +

          ' · ' +

          exposureName;


        /* =====================================================
           OTHER / UNLISTED CHEMICAL
           ===================================================== */

        if (
          chemical === 'other'
        ) {

          confidenceBox.innerHTML =

            '<strong>' +
              'Chemical not currently listed' +
            '</strong><br>' +

            'We do not currently have enough information in this guide to provide a useful material shortlist. Please contact Warlond Plastics with the chemical name, concentration, temperature, exposure conditions and what the finished component needs to do.';


          bestContainer.innerHTML =
            '';

          alternativesContainer.innerHTML =
            '';

          results.classList.add(
            'is-active'
          );

          results.scrollIntoView({

            behavior:
              'smooth',

            block:
              'nearest'

          });

          return;

        }


        /* =====================================================
           GET V3.2 RESULT
           ===================================================== */

        const result =
          getConditionAwareRecommendation(

            chemical,

            concentration,

            temperature,

            exposure

          );


        if (!result) {

          confidenceBox.innerHTML =

            '<strong>' +
              'Further information required' +
            '</strong><br>' +

            'This guide does not currently have enough information to provide a material shortlist for this chemical.';


          bestContainer.innerHTML =
            '';

          alternativesContainer.innerHTML =
            '';

          results.classList.add(
            'is-active'
          );

          return;

        }


        /* =====================================================
           CONFIDENCE MESSAGE
           ===================================================== */

        const confidence =
          getConfidence(

            chemical,

            concentration,

            temperature,

            exposure,

            result

          );


        confidenceBox.innerHTML =

          '<strong>' +
            confidence.title +
          '</strong><br>' +

          confidence.text;


        /* =====================================================
           BEST STARTING OPTION
           ===================================================== */

        bestContainer.innerHTML =

          buildCard(

            result.best,

            'Best starting option',

            result.reason,

            true

          );


        /* =====================================================
           ALTERNATIVE OPTIONS
           ===================================================== */

        alternativesContainer.innerHTML =
          '';


        result.alternatives.forEach(

          function (
            material,
            index
          ) {


            const label =
              index === 0
                ?
                'Alternative option'
                :
                'Another option';


            let description =

              'This material may also be worth considering depending on the concentration, temperature, exposure and mechanical requirements of the finished component.';


            if (
              result.alternativeDescriptions &&
              result.alternativeDescriptions[material]
            ) {

              description =
                result.alternativeDescriptions[material];

            }


            alternativesContainer.insertAdjacentHTML(

              'beforeend',

              buildCard(

                material,

                label,

                description,

                false

              )

            );

          }

        );


        /* =====================================================
           SHOW RESULTS
           ===================================================== */

        results.classList.add(
          'is-active'
        );


        results.scrollIntoView({

          behavior:
            'smooth',

          block:
            'nearest'

        });


      }

    );

  }


  /* ===========================================================
     INITIALISE ALL GUIDES
     =========================================================== */

  function initAllGuides() {

    document
      .querySelectorAll(
        '[data-wp-chemical-guide]'
      )
      .forEach(

        function (guide) {

          initChemicalGuide(
            guide
          );

        }

      );

  }


  /* ===========================================================
     NORMAL PAGE LOAD
     =========================================================== */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(

      'DOMContentLoaded',

      initAllGuides

    );

  }

  else {

    initAllGuides();

  }


  /* ===========================================================
     SHOPIFY THEME EDITOR SUPPORT
     =========================================================== */

  document.addEventListener(

    'shopify:section:load',

    function (event) {


      const guide =

        event.target.querySelector
          ?
          event.target.querySelector(
            '[data-wp-chemical-guide]'
          )
          :
          null;


      if (guide) {

        initChemicalGuide(
          guide
        );

      }


    }

  );


})();